<template>
    <PageLoading v-if="isLoading" />
    <div v-else class="main-wrap checkout-page">
        <h1 class="capitalize-text">{{ $t('general.checkout') }}</h1>
        <q-stepper
            ref="stepper"
            v-model="step"
            animated
            vertical
            flat
            class="checkout-stepper"
        >
            <!-- step 1: billing details -->
            <q-step
                :name="1"
                :title="$t('checkout.billing_details')"
                icon="contact_page"
                :done="step > 1"
            >
                <div class="q-mb-lg">
                    {{ $t('checkout.billing_details_description') }}
                </div>
                <template v-if="isFetchingAddresses">
                    <q-spinner-pie color="primary" size="40px"
                /></template>
                <template v-else>
                    <div v-if="user" class="row q-col-gutter-x-lg">
                        <template
                            v-if="
                                user.userRoles.name == slugs.userRoles.student
                            "
                        >
                            <div class="col-12 col-md-6 col-lg-3 q-pb-lg">
                                <h6>{{ $t('general.name') }}</h6>
                                <h4>{{ user.first_name }}</h4>
                            </div>
                            <!-- <div class="col-12 col-md-6 col-lg-3 q-pb-lg">
                            <h6>{{ $t('general.last_name') }}</h6>
                            <h4>{{ user.last_name }}</h4>
                        </div> -->
                            <div class="col-12 col-md-6 col-lg-3 q-pb-lg">
                                <h6>{{ $t('general.tax_number') }}</h6>
                                <h4>{{ user.tax_number }}</h4>
                            </div>
                        </template>
                        <template v-else>
                            <div class="col-12 col-md-6 col-lg-6 q-pb-lg">
                                <h6>{{ $t('general.company') }}</h6>
                                <h4>
                                    {{ user.affiliations[0].clients.title }}
                                </h4>
                            </div>
                            <div class="col-12 col-md-6 col-lg-3 q-pb-lg">
                                <h6>{{ $t('general.tax_number') }}</h6>
                                <h4>
                                    {{
                                        user.affiliations[0].clients.tax_number
                                    }}
                                </h4>
                            </div>
                        </template>
                        <div class="col-12 col-md-6 col-lg-3 q-pb-lg">
                            <h6>{{ $t('general.tax_address') }}</h6>
                            <div class="row" style="margin-top: -5px">
                                <div class="col-auto">
                                    <q-select
                                        v-if="addressOptions.length"
                                        v-model="addressSelected"
                                        :options="addressOptions"
                                        option-value="id"
                                        option-label="title"
                                        map-options
                                        dense
                                        class="text-h5"
                                        @input="selectAddress"
                                    />
                                    <router-link
                                        v-else
                                        :to="{ name: 'MyTaxAddress' }"
                                        class="text-h5 text-secondary"
                                        >{{
                                            $t('actions.create_tax_address')
                                        }}</router-link
                                    >
                                </div>
                            </div>
                        </div>
                        <template v-if="addressSelected">
                            <div class="col-12 col-md-6 col-lg-3 q-pb-lg">
                                <h6>{{ $t('general.street') }}</h6>
                                <h4>{{ taxAddress.street }}</h4>
                            </div>
                            <div class="col-12 col-md-6 col-lg-3 q-pb-lg">
                                <h6>{{ $t('general.city') }}</h6>
                                <h4>{{ taxAddress.city }}</h4>
                            </div>
                            <div class="col-12 col-md-6 col-lg-3 q-pb-lg">
                                <h6>{{ $t('general.state') }}</h6>
                                <h4>{{ taxAddress.state }}</h4>
                            </div>
                            <div class="col-12 col-md-6 col-lg-3 q-pb-lg">
                                <h6>{{ $t('general.zip_code') }}</h6>
                                <h4>{{ taxAddress.zip_code }}</h4>
                            </div>
                        </template>
                    </div>
                </template>
            </q-step>
            <!-- step 2: payment methods -->
            <q-step
                :name="2"
                :title="$tc('checkout.payment_methods', 2)"
                size="lg"
                icon="paid"
                :done="step > 2"
            >
                <div v-if="paymentMethods.length" class="row items-center">
                    <!-- render radio buttons for available payment methods -->
                    <div class="column col-12">
                        <q-radio
                            v-for="(method, index) in paymentMethods"
                            :key="index"
                            v-model="paymentMethod"
                            :val="method.id"
                            size="xl"
                        >
                            {{
                                JSONParseTranslations(method.title, locale.key)
                            }}
                        </q-radio>
                    </div>
                </div>
                <!-- renders description for the selected payment method -->
                <div
                    v-if="paymentMethods.length && paymentMethod"
                    class="row q-mx-md q-my-md"
                >
                    <h6
                        v-html="
                            paymentMethod
                                ? JSONParseTranslations(
                                      selectedPaymentMethod().description,
                                      locale.key
                                  )
                                : ''
                        "
                    >
                        <!-- {{
                            paymentMethod
                                ? JSONParseTranslations(
                                      selectedPaymentMethod().description,
                                      locale.key
                                  )
                                : ''
                        }} -->
                    </h6>
                </div>
                <!-- if user is client ask if he wants to associate a contract to this purchase -->
                <div
                    v-if="
                        userRole === slugs.userRoles.client && contracts.length
                    "
                    class="row items-center q-mx-md q-mb-md q-mt-xl"
                >
                    <template v-if="isFetchingContracts"
                        ><q-spinner-pie color="primary" size="40px"
                    /></template>
                    <template v-else>
                        <!-- associate contract radio buttons -->
                        <div class="col-6">
                            <span>{{
                                $t(
                                    'checkout.associate_contract_with_this_purchase'
                                )
                            }}</span>
                            <q-radio
                                v-model="withContract"
                                :val="false"
                                :label="$t('general.no')"
                                @input="resetContract"
                            />
                            <q-radio
                                v-model="withContract"
                                :val="true"
                                :label="$t('general.yes')"
                            />
                        </div>
                        <!-- select contract to associate -->
                        <div v-if="withContract" class="col-6 row items-center">
                            <span class="q-mx-md">{{
                                $t('checkout.clients_contract', {
                                    client: contractClient,
                                })
                            }}</span>

                            <div v-if="contracts.length >= 1">
                                <q-select
                                    v-model="contractSelected"
                                    :options="contracts"
                                    :loading="isFetchingContracts"
                                    option-value="id"
                                    option-label="description"
                                    map-options
                                    clearable
                                    dense
                                    outlined
                                    use-input
                                    hide-selected
                                    fill-input
                                    input-debounce="0"
                                    @input="mapContractItemsForTable"
                                />
                            </div>

                            <span v-else class="text-negative">
                                {{ $t('checkout.no_contracts_avaliable') }}
                            </span>
                        </div>
                        <!-- cart items -->
                        <div
                            v-if="withContract && contractSelected"
                            class="col-12"
                        >
                            <ItemsTable
                                :data="cartItems"
                                :columns="cartItemsColumns"
                                :loading="false"
                                :title="$t('checkout.cart_items')"
                                :in-contract="cartItemsInContract"
                                :contract="contractSelected != null"
                            ></ItemsTable>
                        </div>
                        <!-- selected contract items -->
                        <div
                            v-if="withContract && contractSelected"
                            class="col-12"
                        >
                            <ItemsTable
                                :data="mappedSelectedContractItems"
                                :columns="contractItemsColumns"
                                :loading="false"
                                :title="$t('checkout.contract_items')"
                                :hide-bottom="true"
                                :in-contract="cartItemsInContract"
                            ></ItemsTable>
                        </div>
                        <!-- notifications after selecting contract -->
                        <div
                            v-if="withContract && contractSelected"
                            class="col-12 q-mx-md q-mb-md q-mt-xl"
                        >
                            <!-- if there are cart items that exceeds contract available quantities -->
                            <div v-if="hasQuantityAlert()">
                                <h6 class="text-negative">
                                    {{ $t('notify.error_message') }}
                                </h6>
                                <div>
                                    <i18n
                                        path="checkout.errors.quantity_available_exceeded"
                                        tag="div"
                                    >
                                        <q-icon
                                            place="icon"
                                            class=""
                                            name="close"
                                            size="sm"
                                            color="negative"
                                        ></q-icon>
                                        <router-link
                                            :to="{ name: 'Cart' }"
                                            place="cart"
                                            >{{
                                                $t(
                                                    'shopping_cart.shopping_cart'
                                                )
                                            }}</router-link
                                        >
                                    </i18n>
                                </div>
                            </div>
                            <!-- display purchase conditions with a contract selected -->
                            <div v-else>
                                <ul>
                                    <li>
                                        {{
                                            $t('checkout.comercial_conditions')
                                        }}
                                    </li>
                                    <li>
                                        {{
                                            $t(
                                                'checkout.financial_discount_for_this_contract',
                                                {
                                                    discount: contractSelected.global_discount_percentage.toFixed(
                                                        2
                                                    ),
                                                }
                                            )
                                        }}
                                    </li>
                                    <li v-if="cartItemsNotInContract.length">
                                        {{
                                            $t(
                                                'checkout.cart_items_not_in_contract'
                                            )
                                        }}
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <!-- final items to be included in this order -->
                        <div
                            v-if="withContract && contractSelected"
                            class="col-12"
                        >
                            <ItemsTable
                                :data="contractItemsInCart"
                                :columns="contractOrderItemsColumns"
                                :loading="false"
                                :title="
                                    $t(
                                        'checkout.cart_items_after_contract_applied'
                                    )
                                "
                                :selected-contract="contractSelected"
                            ></ItemsTable>
                        </div>
                    </template>
                </div>
            </q-step>
            <!-- step 3: confirm order -->
            <q-step
                :name="3"
                :title="$t('checkout.confirm order')"
                icon="how_to_reg"
                :done="step > 3"
            >
                <div class="q-mb-lg">
                    {{ $t('checkout.confirm_order_description') }}
                </div>
                <div class="row items-end q-col-gutter-lg q-mt-lg">
                    <!-- cart items -->
                    <div class="col-12 col-md-9">
                        <h6>{{ $t('checkout.cart_items') }}</h6>
                        <div class="cart-list">
                            <template v-if="withContract && contractSelected">
                                <div class="q-mb-md">
                                    {{
                                        $t(
                                            'checkout.financial_discount_for_this_contract',
                                            {
                                                discount: contractSelected.global_discount_percentage.toFixed(
                                                    2
                                                ),
                                            }
                                        )
                                    }}
                                </div>
                                <div
                                    v-for="(
                                        cartItem, index
                                    ) in contractItemsInCart"
                                    :key="cartItem.course_id"
                                >
                                    <CourseCartItem
                                        :index="index"
                                        :cart-item="cartItem"
                                        :user="user"
                                        :changes-disallowed="true"
                                    ></CourseCartItem>
                                </div>
                            </template>
                            <template v-else>
                                <div
                                    v-for="(cartItem, index) in cartItems"
                                    :key="cartItem.id"
                                >
                                    <CourseCartItem
                                        :index="index"
                                        :cart-item="cartItem"
                                        :user="user"
                                        :changes-disallowed="true"
                                    ></CourseCartItem>
                                </div>
                            </template>
                        </div>
                    </div>
                    <!-- cart total -->
                    <div class="col-12 col-md-3">
                        <div class="cart-total">
                            <h6>{{ $t('general.total') }}:</h6>
                            <template v-if="withContract && contractSelected">
                                <!-- <span v-if="currency && locale">
                                    <strong>
                                        {{
                                            new Intl.NumberFormat(
                                                currency.definition['locale'],
                                                {
                                                    style: 'currency',
                                                    currency: currency.key,
                                                }
                                            ).format(contractTotal)
                                        }}
                                    </strong>
                                </span> -->
                            </template>
                            <template v-else>
                                <!-- <span v-if="currency && locale">
                                    <strong>
                                        {{
                                            new Intl.NumberFormat(
                                                currency.definition['locale'],
                                                {
                                                    style: 'currency',
                                                    currency: currency.key,
                                                }
                                            ).format(cartTotal)
                                        }}
                                    </strong>
                                </span> -->
                            </template>
                        </div>
                    </div>
                    <!-- payment method -->
                    <div class="col-12 q-pb-lg">
                        <h6>{{ $tc('checkout.payment_methods', 1) }}</h6>
                        <h4 class="text-negative">
                            {{
                                paymentMethod
                                    ? JSONParseTranslations(
                                          selectedPaymentMethod().title,
                                          locale.key
                                      )
                                    : ''
                            }}
                        </h4>
                        <h5
                            v-html="
                                paymentMethod
                                    ? JSONParseTranslations(
                                          selectedPaymentMethod().description,
                                          locale.key
                                      )
                                    : ''
                            "
                        >
                            <!-- {{
                                paymentMethod
                                    ? JSONParseTranslations(
                                          selectedPaymentMethod().description,
                                          locale.key
                                      )
                                    : ''
                            }} -->
                        </h5>
                    </div>
                    <!-- observations -->
                    <div class="col-12 q-pb-lg">
                        <h6>{{ $t('general.observations') }}</h6>
                        <q-input
                            v-model="observation"
                            outlined
                            clearable
                            counter
                            autogrow
                            maxlength="255"
                        />
                    </div>
                </div>
            </q-step>
            <!-- stepper navigation -->
            <template v-slot:navigation>
                <q-stepper-navigation>
                    <div
                        class="row stepper-navigation-buttons space-between justify-end"
                    >
                        <!-- previous button -->
                        <div v-if="step > 1" class="col-12 col-md-auto q-px-sm">
                            <q-btn
                                class="sign-up-btn full-width"
                                color="secondary"
                                size="lg"
                                :label="$t('actions.previous')"
                                flat
                                @click="stepping(-1)"
                            />
                        </div>
                        <!-- next button -->
                        <div
                            v-if="step !== 3"
                            class="col-12 col-md-4 col-xl-3 q-pl-sm"
                        >
                            <q-btn
                                class="sign-up-btn full-width"
                                color="secondary"
                                size="lg"
                                :label="$t('actions.next')"
                                :disable="!nextStepValid()"
                                @click="stepping(1)"
                            />
                        </div>
                        <!-- confirm button -->
                        <div
                            v-if="step === 3"
                            class="col-12 col-md-4 col-xl-3 q-pl-sm"
                        >
                            <q-btn
                                class="sign-up-btn full-width"
                                color="secondary"
                                size="lg"
                                :label="$t('actions.confirm')"
                                :disable="
                                    cartItemsNumber === 0 || isSavingOrder
                                "
                                @click="saveOrder"
                            />
                        </div>
                    </div>
                </q-stepper-navigation>
            </template>
        </q-stepper>
        <q-separator />
    </div>
</template>

<script>
import translate from '@/local-shared/mixins/translate'
import notify from '@/local-shared/mixins/notify'
import validations from '@/views/checkout/mixins/validations'
import PageLoading from '@/components/PageLoading'
import CourseCartItem from '@/components/CourseCartItem'
import ordersService from '@/services/modules/orders'
import contractsService from '@/services/modules/contracts'
import paymentMethodsService from '@/services/modules/paymentMethods'
import wishlistService from '@/services/modules/wishlist'
import coursesService from '@/services/modules/courses'
import taxAddressesService from '@/services/modules/taxAddresses'
import picturesService from '@/services/pictures'
import encoders from '@/helpers/encoders'
import store from '@/store'
import moment from 'moment'
import { slugs } from '@/local-shared/mixins/slugs'
import ItemsTable from '@/components/checkout/ItemsTable'
import ordersTypeService from '@/services/modules/orderstypes'
import currenciesService from '@/services/modules/currencies'

const loadingKeys = {
    isUser: 'is-user',
    fetchingContracts: 'fetching-contracts',
    savingOrder: 'saving-order',
    fetchingPaymentMethods: 'fetching-payment-methods',
    addingToWishlist: 'adding-to-wishlist',
    fetchingCart: 'fetching-cart',
    fetchingAddresses: 'fetching-addresses',
    fetchingOrderTypes: 'fetching-order-types',
    fetchingCurrencies: 'fetching-currencies',
}

export default {
    name: 'Checkout',

    components: {
        PageLoading,
        CourseCartItem,
        ItemsTable,
    },

    mixins: [translate, notify, validations],

    data() {
        return {
            userRole: 'student',
            step: 1,
            paymentMethods: [],
            paymentMethod: null,

            taxAddress: {},
            taxAddresses: [],
            addressSelected: {},
            addressOptions: [],

            contractClient: '',
            contractSelected: null,
            contracts: [],
            mappedSelectedContractItems: [],

            multibox: '',

            discount: 0,
            observation: '',
            orderDate: new Date(),

            cartItems: [],
            totalPrice: 0,

            withContract: false,
            userCurrency: {},

            slugs: slugs,
        }
    },

    computed: {
        isLoading() {
            return (
                this.isUser ||
                this.isFetchingPaymentMethods ||
                this.isFetchingCurrencies
            )
        },

        isFetchingContracts() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingContracts
            ).length
        },

        isUser() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.isUser
            ).length
        },

        locale() {
            return store.getters['locale/currentLocale']
        },

        currency() {
            return store.getters['currency/currentCurrency']
        },

        currencyList() {
            return store.getters['currency/currencyList']
        },

        isFetchingCurrencies() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingCurrencies
            ).length
        },

        user() {
            return store.getters['session/user']
        },

        cart() {
            return store.getters['cart/cart']
        },

        cartItemsNumber() {
            return store.getters['cart/itemCount']
        },

        cartTotal() {
            let cartTotal = 0
            this.cartItems.forEach((cartItem) => {
                this.mapCartItem(cartItem)
                let total = 0
                if (this.currency) {
                    total =
                        parseFloat(
                            JSON.parse(cartItem.price)[this.currency.key]
                        ) * (cartItem.quantity >= 1 ? cartItem.quantity : 1)
                }

                total = total * (1 - cartItem.promotion_discount / 100)
                total = total * (1 + cartItem.vat.value / 100)

                cartTotal = cartTotal + total
            })
            return cartTotal.toFixed(2)
        },

        contractTotal() {
            let cartTotal = 0
            this.contractItemsInCart.forEach((cartItem) => {
                //this.mapCartItem(cartItem)
                let total = 0
                if (this.currency) {
                    total =
                        parseFloat(
                            JSON.parse(cartItem.price)[this.currency.key]
                        ) * (cartItem.quantity >= 1 ? cartItem.quantity : 1)
                }

                total = total * (1 - cartItem.promotion_discount / 100)
                total = total * (1 - cartItem.row_discount_percentage / 100)
                total = total * (1 - cartItem.global_discount / 100)
                total = total * (1 + cartItem.vat.value / 100)

                cartTotal = cartTotal + total
            })
            return cartTotal.toFixed(2)
        },

        mapOrderItems() {
            return this.cartItems.map((cartItem, index) => {
                let order_item = {
                    course_id: cartItem.id,
                    quantity: cartItem.quantity,
                    unit_price: cartItem.price,
                    promotion_discount: cartItem.promotion_discount,
                    discount: 0,
                    item_total: this.mapItemTotal(
                        cartItem.price,
                        cartItem.promotion_discount,
                        cartItem.quantity,
                        cartItem.vat.value
                    ),
                    tax: cartItem.vat.value,
                    preferred_class_id: cartItem.preferred_class_id,
                    rank: index + 1,
                }
                return order_item
            })
        },

        mapContractItems() {
            return this.contractItemsInCart.map((cartItem, index) => {
                let order_item = {
                    course_id: cartItem.course_id,
                    quantity: cartItem.quantity,
                    unit_price: cartItem.price,
                    promotion_discount: cartItem.discount,
                    discount: cartItem.row_discount_percentage,
                    item_total: this.mapContractItemTotal(
                        cartItem.price,
                        cartItem.promotion_discount,
                        cartItem.quantity,
                        cartItem.vat.value,
                        cartItem.row_discount_percentage
                    ),
                    tax: cartItem.vat.value,
                    preferred_class_id: cartItem.preferred_class_id,
                    rank: index + 1,
                }
                return order_item
            })
        },

        isSavingOrder() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.savingOrder
            ).length
        },

        isFetchingPaymentMethods() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingPaymentMethods
            ).length
        },

        isAddingToWishlist() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.addingToWishlist
            ).length
        },

        isFetchingCart() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingCart
            ).length
        },

        isFetchingAddresses() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingAddresses
            ).length
        },

        isFetchingOrderTypes() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingOrderTypes
            ).length
        },

        contractItemsInCart() {
            if (this.mappedSelectedContractItems.length) {
                return this.mappedSelectedContractItems.map((contractItem) => {
                    const matchedCartItem = this.cartItems.find(
                        (cartItem) => cartItem.id === contractItem.course_id
                    )

                    if (matchedCartItem) {
                        return {
                            ...contractItem,
                            quantity: matchedCartItem.quantity,
                            courses_coursetype:
                                matchedCartItem.courses_coursetype,
                            image: matchedCartItem.image,
                            duration: matchedCartItem.duration,
                            global_discount: this.contractSelected
                                .global_discount_percentage,
                        }
                    }

                    return contractItem
                })
            }

            return []
        },

        cartItemsInContract() {
            if (this.cartItems.length) {
                return this.cartItems.filter((item) => {
                    return this.mappedSelectedContractItems.some(
                        (selectedItem) => selectedItem.course_id === item.id
                    )
                })
            }
            return []
        },

        cartItemsNotInContract() {
            if (this.cartItems.length) {
                return this.cartItems.filter((item) => {
                    return this.mappedSelectedContractItems.some(
                        (selectedItem) => selectedItem.course_id !== item.id
                    )
                })
            }
            return []
        },

        cartItemsColumns() {
            return [
                {
                    name: 'title',
                    label: this.$tc('general.courses', 1),
                    field: 'title',
                    align: 'left',
                    sortable: false,
                },
                {
                    name: 'preferred_class_id',
                    label: this.$t('general.preferred_class'),
                    field: 'preferred_class_id',
                    align: 'left',
                    sortable: false,
                },
                {
                    name: 'discount',
                    label: `${this.$t('general.promotion_discount')} (%)`,
                    field: 'discount',
                    align: 'right',
                    sortable: false,
                },
                {
                    name: 'price',
                    label: this.$t('general.price'),
                    field: 'price',
                    align: 'right',
                    sortable: false,
                },
                {
                    name: 'quantity',
                    label: this.$t('general.quantity'),
                    field: 'quantity',
                    align: 'right',
                    sortable: false,
                },
                {
                    name: 'vat',
                    label: this.$t('general.vat'),
                    field: 'tax',
                    align: 'right',
                    sortable: false,
                },
                {
                    name: 'total',
                    label: `${this.$t('general.total')}`,
                    field: 'total',
                    align: 'right',
                    sortable: false,
                },
            ]
        },

        contractItemsColumns() {
            return [
                {
                    name: 'title',
                    label: this.$tc('general.courses', 1),
                    field: 'title',
                    align: 'left',
                    sortable: false,
                },
                {
                    name: 'quantity_available',
                    label: this.$t('general.quantity_available'),
                    field: 'quantity_available',
                    align: 'right',
                    sortable: false,
                },
                {
                    name: 'discount',
                    label: `${this.$t('general.promotion_discount')} (%)`,
                    field: 'discount',
                    align: 'right',
                    sortable: false,
                },
                {
                    name: 'price',
                    label: this.$t('general.price'),
                    field: 'price',
                    align: 'right',
                    sortable: false,
                },
                {
                    name: 'comercial_discount',
                    label: `${this.$t('general.comercial_discount')} (%)`,
                    field: 'row_discount_percentage',
                    align: 'right',
                    sortable: false,
                },
                {
                    name: 'vat',
                    label: this.$t('general.vat'),
                    field: 'tax',
                    align: 'right',
                    sortable: false,
                },
                {
                    name: 'row_total',
                    label: `${this.$t('general.total')}`,
                    field: 'row_total',
                    align: 'right',
                    sortable: false,
                },
            ]
        },

        contractOrderItemsColumns() {
            return [
                {
                    name: 'title',
                    label: this.$tc('general.courses', 1),
                    field: 'title',
                    align: 'left',
                    sortable: false,
                },
                {
                    name: 'discount',
                    label: `${this.$t('general.promotion_discount')} (%)`,
                    field: 'discount',
                    align: 'right',
                    sortable: false,
                },
                {
                    name: 'price',
                    label: this.$t('general.price'),
                    field: 'price',
                    align: 'right',
                    sortable: false,
                },
                {
                    name: 'quantity',
                    label: this.$t('general.quantity'),
                    field: 'quantity',
                    align: 'right',
                    sortable: false,
                },
                {
                    name: 'comercial_discount',
                    label: `${this.$t('general.comercial_discount')} (%)`,
                    field: 'row_discount_percentage',
                    align: 'right',
                    sortable: false,
                },
                {
                    name: 'vat',
                    label: this.$t('general.vat'),
                    field: 'tax',
                    align: 'right',
                    sortable: false,
                },
                {
                    name: 'total',
                    label: `${this.$t('general.total')}`,
                    field: 'total',
                    align: 'right',
                    sortable: false,
                },
            ]
        },
    },

    watch: {
        async cart() {
            await this.fetchCart()
        },
    },

    async created() {
        await this.fetchCart()
        await this.fetchAddresses()
        await this.fetchCurrencies()
    },

    async mounted() {
        await this.fetchPaymentMethods()
        this.paymentMethod = null
        this.userRole = this.user.userRoles.name
    },

    methods: {
        async stepping(stepVal) {
            if (stepVal < 0) {
                this.$refs.stepper.previous()
            } else {
                this.$refs.stepper.next()
            }

            await this.fetchCart()
        },

        nextStepValid() {
            if (this.step === 1) {
                return this.addressSelected ? true : false
            }
            if (this.step === 2) {
                if (this.paymentMethod && !this.withContract) {
                    return true
                }
                if (
                    this.withContract &&
                    this.contractSelected &&
                    !this.hasQuantityAlert()
                ) {
                    return true
                }
                return false
            }
            return false
        },

        itemPrice(price, discount, quantity, vat) {
            let total = price * (1 - discount / 100) * quantity // apply discount
            total = total * (1 + vat / 100) // apply vat
            return parseFloat(total.toFixed(2))
        },

        mapOrderTotal() {
            let orderTotals = {}
            this.currencyList.forEach((currency) => {
                let orderTotal = 0
                this.cartItems.forEach((cartItem) => {
                    let itemTotal = this.mapItemTotal(
                        cartItem.price,
                        cartItem.promotion_discount,
                        cartItem.quantity,
                        cartItem.vat.value
                    )
                    orderTotal =
                        orderTotal +
                        parseFloat(JSON.parse(itemTotal)[currency.key])
                })
                const obj = {
                    [currency.key]: parseFloat(orderTotal.toFixed(2)),
                }
                Object.assign(orderTotals, obj)
            })
            return JSON.stringify(orderTotals)
        },

        mapItemTotal(price, discount, quantity, vat) {
            let itemTotals = {}
            this.currencyList.forEach((currency) => {
                let itemTotal = this.itemPrice(
                    parseFloat(JSON.parse(price)[currency.key]),
                    discount,
                    quantity,
                    vat
                )
                const obj = { [currency.key]: parseFloat(itemTotal.toFixed(2)) }
                Object.assign(itemTotals, obj)
            })
            return JSON.stringify(itemTotals)
        },

        contractItemPrice(price, discount, quantity, vat, rowDiscount) {
            let total = 0
            total = price * quantity
            total = total * (1 - discount / 100) // apply promotion discount
            total = total * (1 - rowDiscount / 100) // apply contract line discount
            total =
                total *
                (1 - this.contractSelected.global_discount_percentage / 100) // apply contract global discount
            total = total * (1 + vat / 100) // apply vat
            return parseFloat(total.toFixed(2))
        },

        mapContractTotal() {
            let orderTotals = {}
            this.currencyList.forEach((currency) => {
                let orderTotal = 0
                this.contractItemsInCart.forEach((cartItem) => {
                    let itemTotal = this.mapContractItemTotal(
                        cartItem.price,
                        cartItem.promotion_discount,
                        cartItem.quantity,
                        cartItem.vat.value,
                        cartItem.row_discount_percentage
                    )
                    orderTotal =
                        orderTotal +
                        parseFloat(JSON.parse(itemTotal)[currency.key])
                })
                const obj = {
                    [currency.key]: parseFloat(orderTotal.toFixed(2)),
                }
                Object.assign(orderTotals, obj)
            })
            return JSON.stringify(orderTotals)
        },

        mapContractItemTotal(price, discount, quantity, vat, rowDiscount) {
            let itemTotals = {}
            this.currencyList.forEach((currency) => {
                let itemTotal = this.contractItemPrice(
                    parseFloat(JSON.parse(price)[currency.key]),
                    discount,
                    quantity,
                    vat,
                    rowDiscount
                )
                const obj = { [currency.key]: parseFloat(itemTotal.toFixed(2)) }
                Object.assign(itemTotals, obj)
            })
            return JSON.stringify(itemTotals)
        },

        selectedPaymentMethod() {
            return this.paymentMethods.find(
                (method) => method.id === this.paymentMethod
            )
        },

        async getContracts() {
            if (this.userRole === this.slugs.userRoles.client) {
                await this.fetchContracts()
            }
        },

        resetContract() {
            this.contractSelected = null
            this.mappedSelectedContractItems = []
        },

        mapContractItemsForTable() {
            this.mappedSelectedContractItems = []
            if (this.contractSelected) {
                // map contract items to correspond to items table fields
                this.contractSelected.items.forEach((contractItem) =>
                    this.mappedSelectedContractItems.push({
                        id: contractItem.id,
                        title: contractItem.course.title,
                        price: contractItem.course_unit_price,
                        on_promotion:
                            Number(contractItem.course_discount_percentage) !=
                            0,
                        promotion_discount: parseFloat(
                            contractItem.course_discount_percentage
                        ),
                        quantity_purchased: contractItem.quantity,
                        quantity_available: contractItem.quantity_available,
                        vat: {
                            value: contractItem.vat_percentage,
                        },
                        vat_value: contractItem.vat_value,
                        row_discount_percentage:
                            contractItem.row_discount_percentage,
                        row_discount_value: contractItem.row_discount_value,
                        row_total: contractItem.total,
                        course_id: contractItem.course.id,
                    })
                )
            }
        },

        hasQuantityAlert() {
            return this.cartItemsInContract.some((cartItem) => {
                const matchingContractItem = this.mappedSelectedContractItems.find(
                    (contractItem) => contractItem.course_id === cartItem.id
                )
                return (
                    matchingContractItem &&
                    cartItem.quantity > matchingContractItem.quantity_available
                )
            })
        },

        async fetchPaymentMethods() {
            this.loadingAdd(loadingKeys.fetchingPaymentMethods)

            let parameters = {
                filter: {
                    application: 'frontend',
                    enabled: true,
                },
            }

            let response = await paymentMethodsService.retrieve(parameters)

            if (response.status === 200) {
                this.paymentMethods = response.data.results
            } else {
                this.notifyError(response)
            }

            this.loadingRemove(loadingKeys.fetchingPaymentMethods)
        },

        async fetchContracts() {
            this.loadingAdd(loadingKeys.fetchingContracts)

            let parameters = {
                filter: {
                    clientId: this.user.affiliations[0].client_id,
                    endDate: moment(new Date()).format('YYYY-MM-DD'),
                    courseIds: this.cartItems.map((item) => item.id),
                },
                relations: {
                    items: {
                        courses: true,
                    },
                },
            }

            let response = await contractsService.retrieve(parameters)

            if (response.status === 200) {
                this.contracts = response.data.results
                if (response.data.results) {
                    this.contractClient = this.user.affiliations[0].clients.title
                }
            } else {
                this.notifyError(response)
            }

            this.loadingRemove(loadingKeys.fetchingContracts)
        },

        async removeFromCart(course) {
            store.dispatch('cart/removeItemFromCart', course)
        },

        async fetchWishlistCount() {
            let parameters = {
                filter: {},
            }

            let response = await wishlistService.count(parameters)

            if (response.status === 200) {
                //update wishlist store counter
                store.dispatch(
                    'wishlist/updateCount',
                    response.data.results[0].count
                )
            }
        },

        async moveToWishlist(course) {
            if (this.user) {
                let response = await this.addToWishlist(course)

                if (response.status === 200) {
                    await this.removeFromCart(course)
                    this.fetchWishlistCount()

                    this.$q.notify({
                        type: 'positive',
                        message: this.$t('success.course_added_to_wishlist'),
                    })
                } else {
                    this.$q.notify({
                        type: 'negative',
                        message: this.$t(
                            'errors.error_adding_course_to_wishlist'
                        ),
                    })
                }
            } else {
                this.$q.notify({
                    type: 'negative',
                    message: this.$t('errors.user_must_be_logged_in'),
                })
            }
        },

        async addToWishlist(course) {
            if (this.isAddingToWishlist) {
                return
            }

            this.loadingAdd(loadingKeys.addingToWishlist)

            let parameters = {
                data: [
                    {
                        course_id: course.id,
                        user_id: this.user.id,
                    },
                ],
            }

            let response = await wishlistService.create(parameters)

            this.loadingRemove(loadingKeys.addingToWishlist)

            return response
        },

        mapCartItem(cartItem) {
            let cartStoreItem = this.cart.find(
                (cart) => cart.id === cartItem.id
            )
            if (cartStoreItem) {
                cartItem.quantity = cartStoreItem.quantity
                cartItem.preferred_class_id = cartStoreItem.preferred_class_id
            }
            return cartItem
        },

        async fetchCart() {
            if (this.isFetchingCart) {
                return
            }

            this.loadingAdd(loadingKeys.fetchingCart)

            let courseIds = this.cart.map((a) => parseInt(a.id))

            let parameters = {
                filter: {
                    active: true,
                    published: true,
                    courseList: courseIds,
                },
                relations: {
                    vat: true,
                    courses_coursetype: true,
                    courses_packagetypes: true,
                },
            }

            let response = await coursesService.retrieve(parameters)

            if (response.status === 200) {
                let cartItems = response.data.results

                await Promise.all(
                    cartItems.map(async (cartItem) => {
                        let imageResponse = await picturesService.retrieveById(
                            cartItem.coursepicture_id
                        )
                        if (imageResponse.status === 200) {
                            cartItem.image = encoders.arrayBufferToBase64(
                                imageResponse
                            )
                        } else {
                            cartItem.image = require('@/assets/logo.svg')
                        }
                    })
                )

                cartItems.forEach((cartItem) => {
                    this.mapCartItem(cartItem)
                })

                this.cartItems = cartItems
            } else {
                //error message
            }

            this.loadingRemove(loadingKeys.fetchingCart)
        },

        async fetchAddresses() {
            this.loadingAdd(loadingKeys.fetchingAddresses)

            let parameters = {
                filter: {},
            }

            if (this.user.userRoles.name === this.slugs.userRoles.client) {
                parameters.relations = { client: true }
                parameters.filter.clientTaxEnabled = true
                parameters.filter.entityId = this.user.affiliations[0].client_id
            }
            if (this.user.userRoles.name === this.slugs.userRoles.student) {
                parameters.relations = { user: true }
                parameters.filter.userTaxEnabled = true
                parameters.filter.entityId = this.user.id
            }

            let response = await taxAddressesService.retrieve(parameters)

            if (response.status === 200) {
                let results = response.data.results
                let addressOptions = []
                results.forEach((r) => {
                    if (r.taxAddress) {
                        addressOptions.push(r.taxAddress)
                    }
                })
                this.addressOptions = addressOptions

                let preferredIndex = this.addressOptions.findIndex(
                    (a) => a.is_preferred === true
                )
                if (preferredIndex != -1) {
                    this.addressSelected = this.addressOptions[preferredIndex]
                } else {
                    this.addressSelected = this.addressOptions.length
                        ? this.addressOptions[0]
                        : null
                }
                this.taxAddress = this.addressSelected
            } else {
                this.notifyError(response)
            }

            this.loadingRemove(loadingKeys.fetchingAddresses)
        },

        selectAddress() {
            this.taxAddress = this.addressSelected
        },

        async saveOrder() {
            this.loadingAdd(loadingKeys.savingOrder)

            let onlineOrderTypeResponse = await this.fetchOrdersTypes()

            if (onlineOrderTypeResponse.status === 200) {
                let parameters = {
                    data: [
                        {
                            orderstatus_id: 1,
                            user_id: this.user.id,
                            ...(this.user.userRoles.name ===
                            this.slugs.userRoles.client
                                ? {
                                      client_id: this.user.affiliations[0]
                                          .client_id,
                                  }
                                : {}),
                            order_date: this.orderDate,
                            discount: this.contractSelected
                                ? this.contractSelected
                                      .global_discount_percentage
                                : 0,
                            payment_method_id: this.paymentMethod,
                            contract_id: this.contractSelected
                                ? this.contractSelected?.id
                                : null,
                            total: this.contractSelected
                                ? this.mapContractTotal()
                                : this.mapOrderTotal(),
                            currency_id: this.currency.id,
                            observation: this.observation,
                            order_items: this.contractSelected
                                ? this.mapContractItems
                                : this.mapOrderItems,
                            street: this.taxAddress.street,
                            city: this.taxAddress.city,
                            state: this.taxAddress.state,
                            zip_code: this.taxAddress.zip_code,
                            tax_number:
                                this.user.userRoles.name ==
                                this.slugs.userRoles.student
                                    ? this.user.tax_number
                                    : this.user.affiliations[0].clients
                                          .tax_number,
                            order_type_id:
                                onlineOrderTypeResponse.data.results[0].id,
                            order_services: [],
                        },
                    ],
                }

                let response = await ordersService.create(parameters)

                if (response.status === 200) {
                    this.$q.notify({
                        type: 'positive',
                        message: this.$t('notify.order_created', {
                            orderId: response.data.data[0].id.id,
                        }),
                    })
                    // if there are other items in cart that not belong to contract purchase
                    if (this.cartItemsNotInContract.length) {
                        // remove from cart items of contract purchased
                        this.cartItemsInContract.forEach((cartItem) =>
                            this.removeFromCart(cartItem)
                        )
                    } else {
                        store.dispatch('cart/emptyCart')
                    }

                    if (response.data.data[0].id.orderInvoiceId) {
                        this.$router.push({
                            name: 'Payment',
                            params: {
                                orderId: response.data.data[0].id.id,
                                invoiceId:
                                    response.data.data[0].id.orderInvoiceId,
                            },
                        })
                    } else {
                        this.$router.push({
                            name: 'OrdersHistory',
                        })
                    }
                } else {
                    this.notifyError(response)
                }
            } else {
                this.notifyError(onlineOrderTypeResponse)
            }

            this.loadingRemove(loadingKeys.savingOrder)
        },

        async fetchOrdersTypes() {
            this.loadingAdd(loadingKeys.fetchingOrderTypes)

            let parameters = {
                filter: {
                    slug: 'online',
                },
            }

            let response = await ordersTypeService.retrieve(parameters)

            this.loadingRemove(loadingKeys.fetchingOrderTypes)

            return response
        },

        async fetchCurrencies() {
            this.loadingAdd(loadingKeys.fetchingCurrencies)

            let filter = {}
            // fetch user currency or client currency if is representant
            if (
                this.user.userRoles &&
                [
                    this.slugs.userRoles.student,
                    this.slugs.userRoles.client,
                ].includes(this.user.userRoles.name)
            ) {
                if (this.user.userRoles.name === this.slugs.userRoles.student) {
                    filter = {
                        id: JSON.parse(
                            this.user.userConfiguration.configuration
                        ).currency,
                    }
                }

                if (this.user.userRoles.name === this.slugs.userRoles.client) {
                    filter = {
                        id: this.user.affiliations[0].clients.currency.id,
                    }
                }

                let parameters = {
                    filter: filter,
                }

                let response = await currenciesService.retrieve(parameters)

                if (response.status === 200) {
                    this.userCurrency = response.data.results[0]
                    store.commit('currency/setCurrency', {
                        id: this.userCurrency.id,
                        key: this.userCurrency.key,
                        name: this.userCurrency.name,
                        definition: {
                            symbol: JSON.parse(this.userCurrency.definition)
                                .symbol,
                            locale: this.locale,
                        },
                    })
                } else {
                    this.notifyError(response)
                }
            } else {
                this.$q.notify({
                    type: 'negative',
                    message: this.$t('notify.could_not_set_user_currency'),
                })
            }

            this.loadingRemove(loadingKeys.fetchingCurrencies)
        },
    },
}
</script>
